import { createSelector } from '@reduxjs/toolkit';
import { ICostCategory, ICostItem } from 'src/common/interfaces/cost-setup';
import type { TRootState } from 'src/store/store-types';

const selectCostSetup = ({ entities }: TRootState) => entities.costSetup;

export const selectCostItemList = createSelector([selectCostSetup], (costSetup) => costSetup.list);
const selectActiveCostItem = createSelector([selectCostItemList], (costList) =>
  costList.find((cost: any) => cost.active),
);

export const selectActiveCostItemQuantity = createSelector([selectActiveCostItem], (cost: any) => cost?.quantity);

export const selectCostCategories = createSelector([selectCostItemList], (costList) =>
  costList.filter((item: ICostCategory) => item.costCategoryId),
);

export const selectCostItems = createSelector([selectCostItemList], (costList) =>
  costList.filter((item: ICostItem) => item.costItemId),
);

export const selectSingleCostItems = createSelector([selectCostItems], (costItems: ICostItem[]) =>
  costItems.filter((item) => !item.parentId),
);

export const selectCostItemsGroupedByTopCategory = createSelector(
  [selectCostCategories, selectCostItems, selectSingleCostItems],
  (categories: ICostCategory[], items: ICostItem[], singleCostItems: ICostItem[]) => {
    const categoryMap = new Map(categories.map((c) => [c.costCategoryId, c]));
    const rootCache = new Map();

    const findRootCategory = (id: string): ICostCategory | null => {
      if (!id) return null;

      if (rootCache.has(id)) return rootCache.get(id);

      const category = categoryMap.get(id);
      if (!category) return null;

      const root = category.parentId ? findRootCategory(category.parentId) : category;
      rootCache.set(id, root);
      return root;
    };

    const result: {
      [key: string]: {
        costCategoryId: string;
        name: string;
        items: { id: string | null; name: string }[];
      };
    } = {};

    items.forEach((item) => {
      const root = findRootCategory(item.parentId);
      if (!root) return;

      if (!result[root.costCategoryId]) {
        result[root.costCategoryId] = {
          costCategoryId: root.costCategoryId,
          name: root.name,
          items: [],
        };
      }

      result[root.costCategoryId].items.push({
        id: item.costItemId,
        name: item.name,
      });
    });

    const resultArray = Object.values(result);
    return singleCostItems.length
      ? [
          ...resultArray,
          {
            costCategoryId: '',
            name: '',
            items: singleCostItems.map((item) => ({
              id: item.costItemId,
              name: item.name,
            })),
          },
        ]
      : resultArray;
  },
);

export const selectOpenCategoryIds = createSelector([selectCostSetup], (costSetup) => costSetup.openCategoryIds);

export const selectSelectedElementId = createSelector([selectCostSetup], (costSetup) => costSetup.selectedElementId);
